<template>
    <b-card :header="headerAlert" :border-variant="value && value.statusStyle"
        :header-bg-variant="value && value.statusStyle" header-text-variant="white">
        <b-card-text>
            <b-row>
                <b-col>
                    <b>Reported by: </b> {{ value.createdBy }} at {{ value.created | formatDate('MM-DD-YYYY h:mm:ss a') }}
                </b-col>
                <b-col>
                    <b>Assigned to: </b> {{ value.assignedTo }}
                </b-col>
                <b-col v-if="value.resolved">
                    <b>Resolved by: </b> {{ value.resolvedBy }} at {{ value.resolved | formatDate('MM-DD-YYYY h:mm:ss a') }}
                </b-col>
                <b-col v-if="value.accepted">
                    <b>Accepted by: </b> {{ value.acceptedBy }} at {{ value.accepted | formatDate('MM-DD-YYYY h:mm:ss a') }}
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b>Reason: </b> {{ value && value.reason }}
                </b-col>
                <b-col>
                    <b>Type: </b> {{ value && value.errorTypeDesc }}
                </b-col>
            </b-row>

            <b>Notes: </b>
            <NotesForm v-if="value.id" :entityId="value.id" :entityType="entityType" :saveButtonLabel="'Add Note'"
                @saved="noteSaved" />

            <NotesList ref="notesList" :paging="false" :entityId="value.id" :entityType="entityType"
                :empty-text="'No notes have been logged for this ' + dispenseErrorLabel" />
        </b-card-text>

        <!-- Buttons -->
        <div v-if="value.statusEnum != statusEnum.Accepted">
            <b-button v-if="buttonTitles.accept && value.statusEnum != statusEnum.Accepted" @click="postNote('accept')"
                variant="primary">
                {{ buttonTitles.accept }}
            </b-button>
            <b-button v-if="buttonTitles.resolve && value.statusEnum != statusEnum.Resolved"
                @click="postNote('resolve')" variant="success">
                {{ buttonTitles.resolve }}
            </b-button>
            <b-button v-if="buttonTitles.awaitingResponse && value.statusEnum != statusEnum.Resolved"
                @click="postNote('awaitingResponse')" variant="warning">
                {{ buttonTitles.awaitingResponse }}
            </b-button>
            <b-button v-if="buttonTitles.reject && value.statusEnum == statusEnum.Resolved" @click="postNote('reject')"
                variant="danger">
                {{ buttonTitles.reject }}
            </b-button>
        </div>

    </b-card>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { DispenseError } from '@/models/DispenseError';
import { Note } from '@/models/Note';
import Axios from 'axios';

import NotesList from '@/components/Note/NotesList.vue'
import NotesForm from '@/components/Note/NotesForm.vue'
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { EntityType } from "@/models/EntityType";

@Component({
    name: "DispensingErrorAlertComponent",
    components: {
        NotesList, NotesForm
    }
})
export default class DispensingErrorAlertComponent extends Vue {
    @Prop() value!: DispenseError;
    @Prop({ default: () => { return { accept: null, reject: null, resolve: null, awaitingResponse: null } } }) private buttonTitles!: any;
    @Prop({ default: 'dispenseAlertConfirmation' }) private confirmationModalId!: string;

    private action: string = '';
    private typeOfErrors = DispenseError.DispenseErrorTypeEnum;
    private statusEnum = DispenseError.DispenseErrorStatusEnum;
    private notes: Array<Note> = [];
    private entityType = EntityType.DispenseError;

    mounted() {
        this.fetchDispenseErrorLabel();
    }

    get headerAlert(): string {
        if (this.value.statusEnum == DispenseError.DispenseErrorStatusEnum.Accepted)
            return "The " + this.dispenseErrorLabel + " was already accepted."
        else if (this.value.statusEnum == DispenseError.DispenseErrorStatusEnum.Resolved)
            return "The " + this.dispenseErrorLabel + " was already resolved. Pending to be accepted."
        else
            return "A " + this.dispenseErrorLabel + " was reported for this prescription."
    }

    get saveButtonLabel(): string {
        return `Save note and ${this.action}`;
    }

    get prefixNote(): string {
        let res = '';
        if (this.action == 'resolve')
            res = "** RESOLVED **";
        if (this.action == 'accept')
            res = "** ACCEPTED **";
        if (this.action == 'reject')
            res = "** REJECTED **";
        return res;
    }

    noteSaved() {
        this.$emit("onNoteSaved");
        this.loadNotes();
    }

    postNote(action: string) {
        this.action = action;
        const note = {} as Note;
        note.body = this.prefixNote;
        Axios.post(`/DispenseError/${this.value.id}/annotation`, note)
            .then(() => {
                this.actionOnDispenseError();
            })
            .catch(err => {
                this.$notification(NotificationOptions.errorSaveNotificationPreset(this.dispenseErrorLabel + " note"));
                console.error('Error while creating notation for the error', { err, response: err.response })
            })
    }

    actionOnDispenseError() {
        Axios.post<DispenseError>(`DispenseError/${this.action}/${this.value.id}`)
            .then(response => {
                if (response.data?.id) {
                    const actionVerb = this.action == 'resolve' ? 'resolved' :
                        this.action == 'awaitingResponse' ? 'Marked Awaiting Response' :
                            (this.action + 'ed');
                    this.$notification(
                        NotificationOptions.notificationOptionsPreset(this.dispenseErrorLabel + ` was ${actionVerb}.`
                            , NotificationOptions.NotificationTypes.success
                        ))
                    this.loadNotes();
                    this.$emit("onStatusChange", this.action, this.value.id);
                }
            })
            .catch(error => {
                console.error(`Error ${this.action}ing error`, { error, response: error?.response });
                this.$notification(NotificationOptions.error(error));
            })
    }

    loadNotes() {
        if (this.$refs.notesList instanceof NotesList)
            this.$refs.notesList.loadNotes();
    }

    protected dispenseErrorLabel = "";

    async fetchDispenseErrorLabel() {
        try {
            const resp = await this.$http.get("/Configuration/DispenseErrorLabel");
            this.dispenseErrorLabel = resp.data?.trim();
        } catch {
            this.dispenseErrorLabel = "Error";
            //ignore
        }
    }
}

</script>

<style scoped></style>
